import type { ValidationPlaybookStep } from "@/validation-playbook";
import { FREE_SAMPLE_SLUG } from "@/product-scope";
import {
  DEFAULT_OPPORTUNITY_USD_CAP_CENTS,
  OPPORTUNITY_BRL_MULTIPLIER,
  OPPORTUNITY_PRICING_VERSION,
  type OpportunityCurrencyOptions,
} from "@/pricing";

export type Locale = "en" | "pt";
export type LocalizedText = Record<Locale, string>;

export interface ProductResearchSource {
  url: string;
  title: string;
  publisher: string;
  type: string;
  published_at: string | null;
  accessed_at: string;
  supports: string[];
}

export interface ReportedCountry {
  code: string;
  name: string;
}

export interface ProductOpportunitySummary {
  status: "draft" | "reviewed";
  freeSample: boolean;
  saleable?: boolean;
  title: LocalizedText;
  teaser?: LocalizedText;
  researchSourceCount?: number;
  outcomeBrief?: LocalizedText;
  historicalOutcome?: {
    disposition: string;
    dispositionConfidence: "confirmed" | "reported" | "inferred" | "unknown";
    causalStatus: "confirmed" | "reported" | "inferred" | "not_publicly_established" | "not_applicable";
    summary: LocalizedText;
    factors: Record<Locale, string[]>;
    sourceUrls: string[];
  };
}

export interface ProductOpportunity extends ProductOpportunitySummary {
  verdict?: {
    decision: "build" | "adapt" | "do_not_build_yet";
    confidence: "high" | "medium" | "low";
    summary: LocalizedText;
    reasons: Record<Locale, string[]>;
  };
  targetCustomer?: LocalizedText;
  purchaseTrigger?: LocalizedText;
  initialOffer?: LocalizedText;
  distribution?: LocalizedText;
  opportunityThesis?: LocalizedText;
  businessModel?: LocalizedText;
  historicalContext?: LocalizedText;
  risks?: Record<Locale, string[]>;
  problem?: LocalizedText;
  whyNow?: LocalizedText;
  wedge?: LocalizedText;
  coreLoop?: LocalizedText;
  smallestTest?: LocalizedText;
  killCriterion?: LocalizedText;
  nonGoals?: Record<Locale, string[]>;
  validationPlan?: Record<Locale, ValidationPlaybookStep[]>;
  researchSources?: Record<Locale, ProductResearchSource[]>;
  evidenceLimits?: Record<Locale, string[]>;
  historicalOutcome?: {
    disposition: string;
    dispositionConfidence: "confirmed" | "reported" | "inferred" | "unknown";
    causalStatus: "confirmed" | "reported" | "inferred" | "not_publicly_established" | "not_applicable";
    summary: LocalizedText;
    factors: Record<Locale, string[]>;
    sourceUrls: string[];
  };
  evidenceState: "editorial_hypothesis" | "source_grounded_draft";
  draftMetadata?: {
    archetype: string;
    derivationVersion: "source-grounded-draft-v1";
    currentPrimaryResearch: "required";
    humanReview: "required";
  };
  pricing: {
    pricingVersion: typeof OPPORTUNITY_PRICING_VERSION;
    currency: "usd";
    supportedCurrencies: ["usd", "brl"];
    brlMultiplier: typeof OPPORTUNITY_BRL_MULTIPLIER;
    startCents: number;
    stepCents: number;
    buyerStep: number;
    capCents: number;
  };
  freePrompt?: LocalizedText;
}

export interface ProductCatalogIndexItem {
  slug: string;
  name: string;
  batch?: string;
  sourceOneLiner?: string;
  reportedStatus?: string;
  primaryCategory?: string;
  countryCodes?: string[];
  logoUrl?: string;
  editorialStatus: "draft" | "reviewed";
  opportunity?: ProductOpportunitySummary;
  searchText?: string;
}

export interface ProductCatalogItem {
  slug: string;
  name: string;
  batch?: string;
  sourceOneLiner?: string;
  reportedStatus?: string;
  primaryCategory?: string;
  categories: string[];
  reportedFounders: Array<{ name: string; title?: string }>;
  foundedYear?: number;
  reportedTeamSize?: number;
  reportedLocation?: string;
  geography?: {
    reportedCountries: ReportedCountry[];
    confidence: "high";
    method: "explicit-country-token";
  };
  website?: string;
  ycUrl?: string;
  logoUrl?: string;
  source: {
    url: string;
    snapshotHash: string;
    fetchedAt: string;
    etag: string | null;
    lastModified: string | null;
    parserVersion: string;
    accessTier: "public-anonymous";
  };
  editorialStatus: "draft" | "reviewed";
  opportunity?: ProductOpportunity;
}

export interface ProductCatalog {
  schemaVersion: "refound-product-catalog-v1";
  generatedAt: string;
  source: {
    runId: string;
    completedAt: string | null;
    schemaVersion: string;
    catalogSha256: string;
  };
  coverage: {
    denominator: string;
    inventoryCompanyCount: number;
    selectedCompanyCount: number;
    derivedCompanyCount: number;
    ratio: number;
    fullInventoryCovered: boolean;
  };
  counts: {
    companies: number;
    reviewed: number;
    drafts: number;
    catalogued: number;
    countries: number;
    categories: number;
  };
  pricing: {
    model: "per-opportunity-linear-confirmed-buyers";
    pricingVersion: typeof OPPORTUNITY_PRICING_VERSION;
    currency: "usd";
    supportedCurrencies: ["usd", "brl"];
    brlMultiplier: typeof OPPORTUNITY_BRL_MULTIPLIER;
    startCents: number;
    stepCents: number;
    buyerStep: number;
    capCents: number;
    localizedAtCheckout: boolean;
  };
  items: ProductCatalogItem[];
}

export interface ProductCatalogIndex extends Omit<ProductCatalog, "schemaVersion" | "items"> {
  schemaVersion: "refound-product-index-v1";
  items: ProductCatalogIndexItem[];
}

interface ProductCatalogDetail {
  schemaVersion: "refound-company-detail-v1";
  generatedAt: string;
  item: ProductCatalogItem;
}

export interface PriceQuote {
  productCode: string;
  opportunitySlug?: string;
  pricingVersion: typeof OPPORTUNITY_PRICING_VERSION;
  currency: "usd";
  priceCents: number;
  formattedPrice: string;
  currencyOptions: OpportunityCurrencyOptions;
  priceTier: number;
  slotIndex?: number;
  confirmedPurchases: number;
  capCents: number;
  capReached: boolean;
}

export const fallbackCatalog: ProductCatalog = {
  schemaVersion: "refound-product-catalog-v1",
  generatedAt: "2026-08-26T00:00:00.000Z",
  source: {
    runId: "interface-fallback",
    completedAt: null,
    schemaVersion: "fallback",
    catalogSha256: "",
  },
  coverage: {
    denominator: "fallback_preview",
    inventoryCompanyCount: 0,
    selectedCompanyCount: 0,
    derivedCompanyCount: 0,
    ratio: 0,
    fullInventoryCovered: false,
  },
  counts: { companies: 0, reviewed: 0, drafts: 0, catalogued: 0, countries: 0, categories: 0 },
  pricing: {
    model: "per-opportunity-linear-confirmed-buyers",
    pricingVersion: OPPORTUNITY_PRICING_VERSION,
    currency: "usd",
    supportedCurrencies: ["usd", "brl"],
    brlMultiplier: OPPORTUNITY_BRL_MULTIPLIER,
    startCents: 100,
    stepCents: 400,
    buyerStep: 1,
    capCents: DEFAULT_OPPORTUNITY_USD_CAP_CENTS,
    localizedAtCheckout: true,
  },
  items: [],
};

export const fallbackCatalogIndex: ProductCatalogIndex = {
  ...fallbackCatalog,
  schemaVersion: "refound-product-index-v1",
  items: fallbackCatalog.items.map((item) => ({
    slug: item.slug,
    name: item.name,
    batch: item.batch,
    sourceOneLiner: item.sourceOneLiner,
    reportedStatus: item.reportedStatus,
    primaryCategory: item.primaryCategory,
    countryCodes: item.geography?.reportedCountries.map(({ code }) => code),
    logoUrl: item.logoUrl,
    editorialStatus: item.editorialStatus,
    opportunity: item.opportunity
      ? {
          status: item.opportunity.status,
          freeSample: item.opportunity.freeSample,
          saleable: item.opportunity.saleable,
          title: item.opportunity.title,
          teaser: item.opportunity.teaser,
          researchSourceCount: item.opportunity.researchSources?.en.length,
          outcomeBrief: item.opportunity.outcomeBrief,
          historicalOutcome: item.opportunity.historicalOutcome,
        }
      : undefined,
  })),
};

function validPublishedIndex(value: unknown): value is ProductCatalogIndex {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const catalog = value as Partial<ProductCatalogIndex>;
  if (catalog.schemaVersion !== "refound-product-index-v1" || !Array.isArray(catalog.items)) return false;
  if (!catalog.counts || catalog.counts.companies !== catalog.items.length) return false;
  if (catalog.counts.reviewed !== catalog.items.length || catalog.counts.drafts !== 0) return false;
  const publishable = catalog.items.length > 0 && catalog.items.every((item) =>
    item.editorialStatus === "reviewed"
      && item.opportunity?.status === "reviewed"
      && item.opportunity.saleable === true
      && typeof item.opportunity.title?.en === "string"
      && typeof item.opportunity.title?.pt === "string",
  );
  const freeSamples = catalog.items.filter((item) => item.opportunity?.freeSample === true);
  return publishable
    && freeSamples.length === 1
    && freeSamples[0]?.slug === FREE_SAMPLE_SLUG
    && Number.isSafeInteger(freeSamples[0]?.opportunity?.researchSourceCount)
    && (freeSamples[0]?.opportunity?.researchSourceCount ?? 0) > 0;
}

export async function loadProductCatalog(signal?: AbortSignal): Promise<{
  catalog: ProductCatalogIndex;
  fallback: boolean;
}> {
  try {
    const response = await fetch("/data/catalog-index.json", {
      headers: { accept: "application/json" },
      cache: "no-store",
      signal,
    });
    if (!response.ok) throw new Error(`catalog_${response.status}`);
    const catalog = await response.json() as unknown;
    if (!validPublishedIndex(catalog)) throw new Error("catalog_publication_contract_invalid");
    return { catalog, fallback: false };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return { catalog: fallbackCatalogIndex, fallback: true };
  }
}

function validDetail(value: unknown, expectedSlug: string): value is ProductCatalogDetail {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const detail = value as Partial<ProductCatalogDetail>;
  return detail.schemaVersion === "refound-company-detail-v1"
    && Boolean(detail.item)
    && detail.item?.slug === expectedSlug
    && typeof detail.item.name === "string"
    && detail.item.name.length > 0
    && typeof detail.item.source?.url === "string"
    && detail.item.editorialStatus === "reviewed"
    && detail.item.opportunity?.status === "reviewed"
    && detail.item.opportunity.saleable === true;
}

export async function loadProductDetail(
  slug: string,
  signal?: AbortSignal,
): Promise<ProductCatalogItem> {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) throw new Error("catalog_detail_slug_invalid");
  const response = await fetch(`/data/companies/${encodeURIComponent(slug)}.json`, {
    headers: { accept: "application/json" },
    cache: "no-store",
    signal,
  });
  if (!response.ok) throw new Error(`catalog_detail_${response.status}`);
  const detail = await response.json() as unknown;
  if (!validDetail(detail, slug)) throw new Error("catalog_detail_schema_invalid");
  return detail.item;
}
